<template>
  <details v-if="years.length" class="record-date-navigation" :open="$vuetify.breakpoint.lgAndUp">
    <summary>
      {{ $t('Date') }}<span v-if="activeYear"> · {{ activeYear }}</span>
      <v-icon small>{{ mdiChevronDown }}</v-icon>
    </summary>
    <nav :aria-label="$t('Filter by date')">
      <button type="button" class="date-show-all" :class="{ selected: !activeYear }" @click="$emit('select', null, null)">
        {{ $t('Show All') }}
      </button>
      <ul class="date-years" :style="availableHeight ? { maxHeight: `${availableHeight}px` } : undefined">
        <li v-for="year in years" :key="year">
          <div class="date-year">
            <button type="button" :class="{ selected: year === activeYear && !activeMonth }" :aria-current="year === activeYear && !activeMonth ? 'date' : undefined" @click="chooseYear(year)">{{ year }}</button>
            <button type="button" class="date-expand" :aria-label="$t('Show months for {{year}}', { year })" :aria-expanded="String(isExpanded(year))" @click="$set(expanded, year, !isExpanded(year))">
              <v-icon small :class="{ expanded: isExpanded(year) }">{{ mdiChevronDown }}</v-icon>
            </button>
          </div>
          <ul v-if="isExpanded(year)" class="date-months">
            <li v-for="month in sortedMonths(year)" :key="month">
              <button type="button" :class="{ selected: year === activeYear && month === activeMonth }" :aria-current="year === activeYear && month === activeMonth ? 'date' : undefined" @click="$emit('select', year, month)">{{ monthLabel(month) }}</button>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </details>
</template>
<script>
import { mdiChevronDown } from '@mdi/js'

export default {
  props: {
    dates: { type: Object, default: () => ({}) },
    activeYear: { type: String, default: null },
    activeMonth: { type: String, default: null },
    descending: Boolean,
  },
  data: () => ({ expanded: {}, mdiChevronDown, availableHeight: null, resizeFrame: null }),
  computed: {
    years() { return Object.keys(this.dates).sort((a, b) => this.descending ? Number(b) - Number(a) : Number(a) - Number(b)) },
  },
  watch: {
    activeYear(value) { if (value) this.$set(this.expanded, value, true) },
    years() { this.$nextTick(this.measureHeight) },
    '$vuetify.breakpoint.lgAndUp'() { this.$nextTick(this.measureHeight) },
  },
  mounted() {
    this.$nextTick(this.measureHeight)
    window.addEventListener('scroll', this.queueMeasure, { passive: true, capture: true })
    window.addEventListener('resize', this.queueMeasure, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.queueMeasure, true)
    window.removeEventListener('resize', this.queueMeasure)
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame)
  },
  methods: {
    queueMeasure() {
      if (this.resizeFrame) return
      this.resizeFrame = window.requestAnimationFrame(() => { this.resizeFrame = null; this.measureHeight() })
    },
    measureHeight() {
      const list = this.$el.querySelector && this.$el.querySelector('.date-years')
      this.availableHeight = list && this.$vuetify.breakpoint.lgAndUp ? Math.max(120, Math.floor(window.innerHeight - list.getBoundingClientRect().top - 24)) : null
    },
    chooseYear(year) { this.$set(this.expanded, year, true); this.$emit('select', year, null) },
    isExpanded(year) { return this.expanded[year] === undefined ? year === this.activeYear : this.expanded[year] },
    sortedMonths(year) { return [...this.dates[year]].sort((a, b) => this.descending ? Number(b) - Number(a) : Number(a) - Number(b)) },
    monthLabel(month) { return new Intl.DateTimeFormat(this.$locale(), { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, Number(month) - 1, 1))) },
  },
}
</script>
<style>
.record-date-layout { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; width: 100%; }
.record-date-layout__main { min-width: 0; grid-row: 2; }
.record-date-navigation { grid-row: 1; min-width: 0; text-align: left; justify-self: end; width: 100%; }
.record-date-navigation summary { display: flex; align-items: center; justify-content: flex-end; gap: 8px; cursor: pointer; padding: 10px 12px; font-size: 14px; }
.record-date-navigation summary::-webkit-details-marker { display: none; }
.record-date-navigation nav { padding: 8px; border: 1px solid rgba(128,128,128,.25); border-radius: 8px; }
.record-date-navigation ul { list-style: none; margin: 0; padding: 0; }
.record-date-navigation .date-years { max-height: min(60vh, 520px); overflow-y: auto; scrollbar-gutter: stable; scrollbar-width: auto; }
.record-date-navigation .date-years::-webkit-scrollbar { width: 8px; }
.record-date-navigation .date-years::-webkit-scrollbar-thumb { background: rgba(128,128,128,.45); border-radius: 6px; }
.record-date-navigation button { font: inherit; font-size: 14px; color: inherit; text-align: left; border-radius: 4px; min-height: 38px; padding: 8px 12px; cursor: pointer; }
.record-date-navigation button:hover { background: rgba(145,85,253,.08); }
.record-date-navigation button:focus-visible, .record-date-navigation summary:focus-visible { outline: 2px solid var(--v-primary-base, #9155fd); outline-offset: 1px; }
.record-date-navigation .selected { color: var(--v-primary-base, #9155fd); font-weight: 600; background: rgba(145,85,253,.06); }
.record-date-navigation .date-show-all { width: 100%; margin-bottom: 6px; border-bottom: 1px solid rgba(128,128,128,.2); border-radius: 0; }
.record-date-navigation .date-year { display: flex; }
.record-date-navigation .date-year > button:first-child { flex: 1; }
.record-date-navigation .date-expand { flex: 0 0 38px; padding: 8px; }
.record-date-navigation .expanded { transform: rotate(180deg); }
.record-date-navigation .date-months { padding-left: 12px; }
.record-date-navigation .date-months button { width: 100%; min-height: 34px; }
@media (min-width: 1264px) {
  .record-date-layout--with-dates { grid-template-columns: minmax(0, 1fr) 180px; gap: 24px; }
  .record-date-layout__main { grid-row: 1; grid-column: 1; }
  .record-date-navigation { grid-row: 1; grid-column: 2; position: sticky; top: 88px; align-self: start; }
  .record-date-navigation summary { display: none; }
  .record-date-navigation .date-years { max-height: calc(100vh - 180px); max-height: calc(100dvh - 180px); }
}
</style>
